/**
 * Phone normalisation and SK ID formatting.
 */

/**
 * Reduce a phone number to digits only, so "0101 234 5678", "+20 101 234 5678"
 * and "٠١٠١٢٣٤٥٦٧٨" all compare equal when checking for duplicates.
 *
 * Egyptian numbers are written both as `01xxxxxxxxx` locally and `+201xxxxxxxxx`
 * internationally. We canonicalise the international form to the local one,
 * otherwise the same member entered two ways would never be flagged as a
 * duplicate — which is the whole point of normalising.
 */
export function normalizePhone(input: string): string {
  if (typeof input !== 'string') return '';

  // Arabic-Indic and Extended Arabic-Indic digits to ASCII.
  const ascii = input.replace(/[٠-٩۰-۹]/g, (d) => {
    const code = d.charCodeAt(0);
    return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660);
  });

  const digits = ascii.replace(/\D/g, '');

  // +20 1xx xxx xxxx  ->  01xx xxx xxxx
  if (digits.startsWith('20') && digits.length === 12) return `0${digits.slice(2)}`;
  // 0020 prefix, occasionally typed on landline handsets.
  if (digits.startsWith('0020') && digits.length === 14) return `0${digits.slice(4)}`;

  return digits;
}

/**
 * Loose validity check. Deliberately permissive — the front desk must never be
 * blocked from saving a member because a number looks unusual (foreign
 * students, landlines, a parent's number for a kid). Used only to show a hint.
 */
export function looksLikeEgyptianMobile(phone: string): boolean {
  return /^01[0125]\d{8}$/.test(normalizePhone(phone));
}

/** Display grouping: `0101 234 5678`. Falls back to the raw value. */
export function formatPhone(phone: string): string {
  const d = normalizePhone(phone);
  if (d.length !== 11) return d || '—';
  return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
}

/* -------------------------------------------------------------------------
   SK IDs
   ------------------------------------------------------------------------- */

export const SK_ID_PREFIX = 'SK-';
const SK_ID_PAD = 4;

/**
 * `1` -> `SK-0001`. Past 9999 the number simply grows to `SK-10000` rather
 * than wrapping or truncating — an academy outgrowing four digits is a good
 * problem, and silently reusing IDs would be a terrible one.
 */
export function formatSkId(sequence: number): string {
  return `${SK_ID_PREFIX}${String(Math.max(1, Math.trunc(sequence))).padStart(SK_ID_PAD, '0')}`;
}

/** `SK-0042` -> `42`. Returns null for anything that is not an SK ID. */
export function parseSkId(skId: string): number | null {
  const m = /^SK-(\d+)$/i.exec(skId.trim());
  if (!m || m[1] === undefined) return null;
  const n = Number(m[1]);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/**
 * The highest sequence number in a set of existing SK IDs — used to seed the
 * local counter so a reinstalled device never re-issues an ID that is already
 * in use. Unparseable IDs are ignored rather than throwing.
 */
export function highestSkSequence(skIds: readonly string[]): number {
  let highest = 0;
  for (const id of skIds) {
    const n = parseSkId(id);
    if (n !== null && n > highest) highest = n;
  }
  return highest;
}

/** Normalise what someone typed into the search box so `sk 42` finds `SK-0042`. */
export function normalizeSkIdQuery(input: string): string | null {
  const compact = input.trim().replace(/\s+/g, '').replace(/^sk-?/i, '');
  if (!/^\d+$/.test(compact)) return null;
  return formatSkId(Number(compact));
}
